import { useEffect, type ReactNode } from 'react';

import {
  LOCALIZED_PAGES,
  localizedPath,
  SITE_COPY,
  SUPPORTED_LANGUAGES,
  type Language,
  type LocalizedPage,
} from '@lifetimeline/shared';

import { useI18n } from './index.js';

/**
 * 언어별 주소를 갖는 화면(`/ko`, `/en/privacy`)을 감싼다. **주소가 곧 언어다.**
 *
 * 첫 화면은 `decideLanguage`가 이미 주소를 보고 정했다. 여기서 맞추는 것은
 * 앱 안에서 `/en` → `/ko`로 옮겨 갈 때다 — 그때는 페이지가 다시 안 뜬다.
 *
 * 저장하지 않는다(`persist: false`). 검색 결과에서 `/en`을 한 번 눌렀다고
 * 그 사람의 언어를 영어로 바꾸면 안 된다 — 저장은 사람이 고를 때만.
 */
export function LocalizedRoute({
  language,
  page,
  children,
}: {
  language: Language;
  page: LocalizedPage;
  children: ReactNode;
}) {
  const { language: current, setLanguage } = useI18n();

  useEffect(() => {
    if (current !== language) {
      setLanguage(language, { persist: false });
    }
  }, [current, language, setLanguage]);

  /*
   * 탭 제목. 개인정보처리방침은 자기 제목이 있다(HTML에 박힌 것과 같은 값).
   *
   * 언어가 **실제로 바뀐 뒤에** 얹는다(`current`). 언어가 바뀌면 I18nProvider가
   * 제목을 서비스 소개로 되돌리므로, 그보다 먼저 얹으면 덮인다.
   */
  useEffect(() => {
    if (current !== language) {
      return;
    }
    const copy = SITE_COPY[language];
    document.title = page === '/privacy' ? copy.privacyTitle : copy.title;
  }, [current, language, page]);

  return <>{children}</>;
}

/** 언어 × 고정 화면의 모든 조합. 라우터가 이 목록으로 경로를 만든다 */
export const LOCALIZED_ROUTES = SUPPORTED_LANGUAGES.flatMap((language) =>
  LOCALIZED_PAGES.map((page) => ({
    language,
    page,
    path: localizedPath(language, page),
  })),
);
